/**
 * CalendarLinkPrompt
 *
 * Shown after a user links a calendar to a family member.
 * Prompts them to toggle calendar visibility via the side drawer.
 */

import { Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideOutDown,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getTypographyStyle } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type CalendarLinkPromptProps = {
  visible: boolean;
  calendarName: string;
  onShowCalendars: () => void;
  onDismiss: () => void;
};

export function CalendarLinkPrompt({
  visible,
  calendarName,
  onShowCalendars,
  onDismiss,
}: CalendarLinkPromptProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          style={styles.backdrop}
        >
          <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
        </Animated.View>

        <Animated.View
          entering={SlideInDown.duration(250)}
          exiting={SlideOutDown.duration(180)}
          style={[
            styles.card,
            { backgroundColor: palette.cardBackground },
          ]}
        >
          <View
            style={[styles.iconCircle, { backgroundColor: palette.borderMuted }]}
          >
            <Ionicons name="eye-outline" size={28} color={palette.primary} />
          </View>

          <Text style={[styles.title, { color: palette.text }]}>
            Show on your calendar?
          </Text>

          <Text style={[styles.message, { color: palette.textMuted }]}>
            Would you like to see &quot;{calendarName}&quot; events in your FimFam
            calendar view?
          </Text>

          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[styles.button, { borderColor: palette.border }]}
              onPress={onDismiss}
              activeOpacity={0.7}
            >
              <Text style={[styles.buttonText, { color: palette.textMuted }]}>
                Not now
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.primaryButton,
                { backgroundColor: palette.primary },
              ]}
              onPress={onShowCalendars}
              activeOpacity={0.7}
            >
              <Text style={[styles.buttonText, { color: '#FFFFFF' }]}>
                Show me
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const titleStyle = getTypographyStyle('heading');
const bodyStyle = getTypographyStyle('body');
const buttonStyle = getTypographyStyle('subheading');

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  card: {
    width: '85%',
    maxWidth: 340,
    borderRadius: BorderRadius.lg,
    padding: Spacing.xxl,
    alignItems: 'center',
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: BorderRadius.full,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  title: {
    ...titleStyle,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: Spacing.sm,
  },
  message: {
    ...bodyStyle,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: Spacing.xxl,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: Spacing.md,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.sm,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  primaryButton: {
    borderWidth: 0,
  },
  buttonText: {
    ...buttonStyle,
  },
});
